/**
 * cihazlarim.tsx
 *
 * NE İŞE YARAR : Kayıtlı cihazlar listesi ve cihaz kaldırma — `M2-09` ve `M2-10`
 * KAYNAK SPEC  : 15-arayuz-standardi.md §15 (Cihazlarım) · §9 (durum ekranları)
 *                §11 (onay ekranları) · §12 (tarih ve saat) · §8.2 (liste kuralları)
 *                04-api-sozlesmesi.md M2-09 · M2-10 · ADR-019 §5 · `04` §7 (idempotency)
 * BAĞLI OLDUĞU : src/oturum/baglam.tsx · src/oturum/depo.ts · src/metin
 * KULLANAN     : src/uygulama.tsx — `/cihazlarim` yolu (korumalı)
 * DİKKAT       : 1) `is_current` SUNUCUDAN GELİR, HESAPLANMAZ (`04` M2-09). İstemci
 *                   hesaplasaydı cihaz sırrını elinde tutup karşılaştırması gerekirdi;
 *                   sır ise yalnız saklanır, hiç okunup kıyaslanmaz.
 *                2) MEVCUT CİHAZI KALDIRMAK OTURUMU KAPATIR (`04` M2-10 · ADR-019 §5) ve
 *                   bu **beklenen davranıştır**. Onay kutusu bunu ayrıca yazar; yazmasaydı
 *                   kullanıcı kendini bir arıza sanacağı bir yerde bulurdu.
 *                3) KALDIRMA `Idempotency-Key` İSTER (`04` §7 — uçta `@IdempotencyZorunlu`).
 *                   Anahtar **denemede bir kez** üretilir ve yeniden denemede aynısı
 *                   gönderilir; her denemede yenilenseydi anahtarın varlık sebebi
 *                   ortadan kalkardı.
 *                4) KENDİ CİHAZINDA GEREKÇE ZORUNLU DEĞİLDİR (`04` M2-10 — `nullif`).
 *                   `M2-12`de (başkasının cihazı) zorunludur ve o ekran IAM modülünün
 *                   işidir; buraya gerekçe alanı konsaydı kullanıcı kendi cihazını
 *                   silmek için kendine hesap vermek zorunda kalırdı.
 *                5) YEREL CİHAZ SIRRI, MEVCUT CİHAZ KALDIRILDIĞINDA SİLİNİR. Bırakılsaydı
 *                   tarayıcı, sunucuda artık karşılığı olmayan bir sırrı her girişte
 *                   boşuna göndermeye devam ederdi.
 *                6) ALTI DURUMUN DÖRDÜ BURADA VAR (`15` §9): yükleniyor **iskelet**tir
 *                   (dönen çark değil), boş durum ne yapılacağını söyler, hata ne
 *                   olduğunu ve ne yapılacağını söyler, bağlantı yok yeniden dene
 *                   sunar. *"Yetkisiz"* ve *"bulunamadı"* bu ekranda oluşamaz: liste
 *                   yalnız çağıranın kendi cihazlarıdır ve izin gerektirmez (`SELF`).
 *                7) EKRAN MENÜDE DEĞİL, KULLANICI MENÜSÜNDEDİR (`kabuk.tsx`). `SELF`
 *                   bir ekrandır ve izin gerektirmez; modül menüsü ise *"ne
 *                   yapabiliyorsan o"* listesidir (`15` §5.2) ve kendi cihazını
 *                   yönetmek bir yetki değildir.
 *                8) BAŞLIK VE ÇERÇEVE KABUKTAN GELİR (2026-08-31 04:35:00). Ekran
 *                   yazıldığında kabuk henüz yoktu ve kendi çerçevesini çiziyordu;
 *                   kabuk gelince `SayfaBasligi`ye ve kabuğun dolgusuna bırakıldı.
 *                   İkisi birden dursaydı sayfada iki başlık ve çift dolgu olurdu.
 *                9) SÜTUNLAR `sutunlariSuz` İLE SÜZÜLÜR (`15` §7.4). İlk yazımda
 *                   `display_name` için `undefined ? 'Adsız cihaz'` yazılıydı ve bu tam
 *                   olarak `15` §7.4'ün yasakladığı şeydi: **yanıtta bulunmayan** bir
 *                   alanı boş değer sanıp yerine bir metin koymak, o alanın var
 *                   olduğunu söyler. Artık ayrım net — alan yoksa **sütun hiç
 *                   çizilmez**, alan var ama boşsa *"—"* yazılır.
 * SON GÜNCELLEME: 2026-08-31 13:05:00
 */
import {
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiHatasi } from '../../api/hata';
import { yeniIdempotencyKey } from '../../api/istemci';
import { metin, tarihSaat } from '../../metin';
import { useOturum } from '../../oturum/baglam';
import { cihazSirriniSil } from '../../oturum/depo';
import { alanDegeri, alanMetni } from '../../yetki/alan';
import { sutunlariSuz, type Sutun } from '../../yetki/sutun';
import { HataUyarisi } from '../ortak/hata-uyarisi';
import { SayfaBasligi } from '../ortak/sayfa-basligi';

/** `04` M2-09 yanıtındaki cihaz satırı — alan politikası bazı alanları gizleyebilir. */
interface Cihaz extends Record<string, unknown> {
  readonly id: string;
  readonly device_type?: string;
  readonly display_name?: string | null;
  readonly registered_at?: string | null;
  readonly last_seen_at?: string | null;
  readonly expires_at?: string | null;
  readonly is_current: boolean;
}

/**
 * `M2-09` yanıtındaki sütunlar (`04` M2-09 · `03` §4.2.2).
 *
 * `izin` yazılmadı çünkü `M2-09` `SELF`tir: kendi cihazını görmek izin gerektirmez.
 * Sütunun çizilip çizilmeyeceğini **yanıtın kendisi** söyler — alan geldiyse sütun
 * vardır (`sutun.ts` notu 1).
 */
const SUTUNLAR: readonly Sutun<Cihaz>[] = [
  {
    ad: 'display_name',
    baslik: 'cihazlar.ad',
    // Ad kullanıcının serbest yazdığı metindir ve boş bırakılabilir (`03` §4.2.2);
    // **boş** olması ile **görünmemesi** ayrı şeylerdir — birincisi burada karşılanır.
    hucre: (cihaz) => alanMetni(cihaz['display_name']),
  },
  {
    ad: 'device_type',
    baslik: 'cihazlar.tur',
    hucre: (cihaz) =>
      cihaz['device_type'] === 'MOBILE' ? metin('cihazlar.turMobil') : metin('cihazlar.turWeb'),
  },
  {
    ad: 'last_seen_at',
    baslik: 'cihazlar.sonGorulme',
    // Tarih biçimi tek yerdedir (`15` §12) ve boş değerde çalıştırılmaz.
    hucre: (cihaz) => alanMetni(cihaz['last_seen_at'], (d) => tarihSaat(String(d))),
    sayi: true,
  },
  {
    ad: 'expires_at',
    baslik: 'cihazlar.gecerlilik',
    hucre: (cihaz) => alanMetni(cihaz['expires_at'], (d) => tarihSaat(String(d))),
    sayi: true,
  },
];

export function CihazlarimEkrani(): ReactElement {
  const { cagir, cikisYap, izinVar } = useOturum();
  const yonlendir = useNavigate();

  const [cihazlar, setCihazlar] = useState<readonly Cihaz[] | null>(null);
  const [hata, setHata] = useState<unknown>(undefined);
  const [kaldirilacak, setKaldirilacak] = useState<Cihaz | null>(null);
  const [kaldiriliyor, setKaldiriliyor] = useState(false);

  const listeyiCek = useCallback(async (): Promise<void> => {
    setHata(undefined);
    setCihazlar(null);
    try {
      const yanit = await cagir<{ items: readonly Cihaz[] }>({ yol: '/me/devices' });
      setCihazlar(yanit.items);
    } catch (sebep) {
      setHata(sebep);
    }
  }, [cagir]);

  useEffect(() => {
    // Açılışta veri çekme: `cek` beklemeden önce yükleniyor durumunu yazar
    // (68. adım gözden geçirmesi — dış sistemle eşitleme).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void listeyiCek();
  }, [listeyiCek]);

  async function kaldir(cihaz: Cihaz): Promise<void> {
    setKaldiriliyor(true);
    try {
      await cagir({
        yol: `/me/devices/${encodeURIComponent(cihaz.id)}/revoke`,
        yontem: 'POST',
        govde: {},
        // Başlıktaki not 3 — anahtar bu deneme için bir kez üretilir.
        idempotencyKey: yeniIdempotencyKey(),
      });

      setKaldirilacak(null);

      if (cihaz.is_current) {
        // Başlıktaki not 2 ve 5 — sunucu oturumu kapattı; yerel kopyalar temizlenir.
        cihazSirriniSil();
        await cikisYap();
        // `void` — yönlendirici bir `Promise` döndürür; beklenecek bir sonucu yoktur.
        void yonlendir('/giris', { replace: true });
        return;
      }

      notifications.show({ color: 'basari', message: metin('cihazlar.kaldirildi') });
      await listeyiCek();
    } catch (sebep) {
      setKaldirilacak(null);
      notifications.show({
        color: 'hata',
        message: sebep instanceof ApiHatasi ? sebep.message : metin('hata.beklenmeyen'),
      });
    } finally {
      setKaldiriliyor(false);
    }
  }

  return (
    <Container size="md" px={0}>
      <SayfaBasligi baslik={metin('cihazlar.baslik')} aciklama={metin('cihazlar.aciklama')} />

      <Card withBorder radius="sm" padding="lg">
        <ListeGovdesi
          cihazlar={cihazlar}
          hata={hata}
          yenidenDene={() => void listeyiCek()}
          kaldirmayiIste={setKaldirilacak}
          izinVar={izinVar}
        />
      </Card>

      {/* `15` §11 — sonucu geri alınamayan değil ama **anında oturum kapatan** bir
          işlem; onay kutusu ne olacağını açıkça yazar ve iptal varsayılan seçenektir. */}
      <Modal
        opened={kaldirilacak !== null}
        onClose={() => setKaldirilacak(null)}
        title={metin('cihazlar.kaldirBaslik')}
        radius="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {kaldirilacak?.is_current === true
              ? metin('cihazlar.kaldirKendisiMetin')
              : metin('cihazlar.kaldirMetin')}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setKaldirilacak(null)}>
              {metin('ortak.iptal')}
            </Button>
            <Button
              color="hata"
              loading={kaldiriliyor}
              onClick={() => {
                if (kaldirilacak !== null) {
                  void kaldir(kaldirilacak);
                }
              }}
            >
              {metin('cihazlar.kaldir')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

/** `15` §9'un durumları — yükleniyor, hata, boş, dolu (başlıktaki not 6). */
function ListeGovdesi({
  cihazlar,
  hata,
  yenidenDene,
  kaldirmayiIste,
  izinVar,
}: {
  cihazlar: readonly Cihaz[] | null;
  izinVar: (kod: string) => boolean;
  hata: unknown;
  yenidenDene: () => void;
  kaldirmayiIste: (cihaz: Cihaz) => void;
}): ReactElement {
  if (hata !== undefined) {
    return (
      <Stack gap="md">
        <HataUyarisi hata={hata} />
        <Group>
          <Button variant="default" onClick={yenidenDene}>
            {metin('ortak.yenidenDene')}
          </Button>
        </Group>
      </Stack>
    );
  }

  if (cihazlar === null) {
    // İSKELET, dönen çark değil (`15` §9): sayfanın yapısı korunur ve içerik geldiğinde
    // satırlar yerine oturur; çark, yerleşimin sıçramasına yol açardı.
    return (
      <Stack gap="sm">
        <Skeleton height={20} radius="sm" />
        <Skeleton height={20} radius="sm" />
        <Skeleton height={20} radius="sm" />
      </Stack>
    );
  }

  // `15` §7.1 ve §7.4 — izni olmayan **ve** yanıtta bulunmayan sütun çizilmez.
  const sutunlar: readonly Sutun<Cihaz>[] = sutunlariSuz(SUTUNLAR, cihazlar ?? [], izinVar);

  if (cihazlar.length === 0) {
    // Boş durum ne olduğunu **ve** sonucunu söyler; *"kayıt yok"* yetmez (`15` §9).
    return (
      <Center py="xl">
        <Text size="sm" c="notr.7" ta="center" maw={420}>
          {metin('cihazlar.bos')}
        </Text>
      </Center>
    );
  }

  return (
    <Table.ScrollContainer minWidth={640}>
      <Table withRowBorders verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            {sutunlar.map((sutun) => (
              <Table.Th key={sutun.ad}>{metin(sutun.baslik)}</Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {cihazlar.map((cihaz) => (
            <Table.Tr key={cihaz.id}>
              {sutunlar.map((sutun) => (
                <Table.Td key={sutun.ad}>
                  <Group gap="xs" wrap="nowrap">
                    <Text
                      size="sm"
                      style={
                        sutun.sayi === true ? { fontVariantNumeric: 'tabular-nums' } : undefined
                      }
                    >
                      {sutun.hucre === undefined
                        ? alanMetni(alanDegeri(cihaz, sutun.ad))
                        : sutun.hucre(cihaz)}
                    </Text>
                    {/* Rozet yalnız ad sütununda ve `is_current` sunucudan gelir (not 1). */}
                    {sutun.ad === 'display_name' && cihaz.is_current ? (
                      <Badge color="bilgi" variant="light" size="sm">
                        {metin('cihazlar.buCihaz')}
                      </Badge>
                    ) : null}
                  </Group>
                </Table.Td>
              ))}
              <Table.Td ta="right">
                <Button
                  variant="subtle"
                  color="hata"
                  size="compact-sm"
                  onClick={() => kaldirmayiIste(cihaz)}
                >
                  {metin('cihazlar.kaldir')}
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
